import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, ArrowLeft, ArrowUpRight, Download } from "lucide-react";
import Reveal from "../components/Reveal";
import RequestQuoteModal from "../components/RequestQuoteModal";
import { findAnyProduct } from "../data/catalogue";

export default function ProductDetail() {
  const { slug } = useParams();
  const [quoteOpen, setQuoteOpen] = useState(false);

  const match = useMemo(() => findAnyProduct(slug), [slug]);

  if (!match) {
    return (
      <div className="pt-40 pb-24 container-luxe text-center">
        <h2 className="font-serif text-4xl text-brand-charcoal">Product not found.</h2>
        <Link to="/products" className="mt-8 luxe-btn-outline text-brand-green border-brand-green hover:bg-brand-green hover:text-brand-ivory inline-flex">
          Browse Catalogue <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    );
  }

  const { product, base, label, siblings } = match;
  const related = siblings.filter((p) => p.slug !== slug).slice(0, 4);

  const specs = [
    { k: "Botanical", v: product.tagline },
    { k: "Origin", v: product.origin },
    { k: "Grade", v: product.grade },
    { k: "Moisture", v: product.moisture },
    ...(product.purity && product.purity !== "—" ? [{ k: "Purity", v: product.purity }] : []),
    { k: "Shelf Life", v: product.shelf_life },
    { k: "Storage", v: product.storage },
    { k: "Packaging", v: product.packaging },
    { k: "MOQ", v: product.moq },
  ];

  return (
    <>
      {/* HERO */}
      <section className="pt-32 pb-16 md:pt-40 bg-brand-ivory-warm relative overflow-hidden">
        <div className="container-luxe grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <Reveal className="lg:col-span-7 order-2 lg:order-1">
            <Link to={base} className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-brand-charcoal/60 hover:text-brand-green mb-8" data-testid="product-back">
              <ArrowLeft className="w-3.5 h-3.5" /> All {label}
            </Link>
            <span className="overline">{product.grade.split("/")[0].trim()} · Export Grade</span>
            <h1 className="font-serif text-5xl md:text-6xl mt-4 leading-[1.02] text-brand-charcoal">{product.name}</h1>
            <div className="italic text-brand-charcoal/50 mt-2 font-serif text-xl">{product.tagline}</div>
            <span className="divider-gold" />
            <p className="text-brand-charcoal/75 text-[17px] leading-[1.8] max-w-lg">{product.description}</p>
            <div className="mt-10 flex flex-wrap gap-3">
              <button onClick={() => setQuoteOpen(true)} className="luxe-btn-primary" data-testid="product-request-quote">
                Request Quote <ArrowRight className="w-4 h-4" />
              </button>
              <a href={`mailto:info@deeprootsspice.com?subject=Specification Sheet Request — ${product.name}`} className="luxe-btn-outline text-brand-green border-brand-green hover:bg-brand-green hover:text-brand-ivory" data-testid="product-download-spec">
                <Download className="w-4 h-4" /> Specification Sheet
              </a>
            </div>
          </Reveal>
          <Reveal delay={100} className="lg:col-span-5 lg:col-start-8 order-1 lg:order-2">
            <div className="relative aspect-[4/5] overflow-hidden bg-brand-ink border border-brand-gold/30">
              <div className="absolute inset-0" style={{ backgroundColor: product.color, opacity: 0.3 }} />
              <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
              <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between text-brand-ivory/70 text-[10px] tracking-[0.28em] uppercase">
                <span>Ref · {product.slug}</span>
                <span>India</span>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SPECS + APPLICATIONS */}
      <section className="py-24 md:py-32 bg-brand-ivory">
        <div className="container-luxe grid lg:grid-cols-12 gap-16">
          <Reveal className="lg:col-span-7">
            <span className="overline">Specifications</span>
            <h3 className="font-serif text-3xl md:text-4xl mt-4 text-brand-charcoal mb-10">Technical profile.</h3>
            <dl className="divide-y divide-brand-charcoal/10 border-t border-brand-charcoal/10">
              {specs.map((s) => (
                <div key={s.k} className="grid grid-cols-3 py-5 gap-6 items-baseline">
                  <dt className="text-[11px] tracking-[0.24em] uppercase text-brand-charcoal/50">{s.k}</dt>
                  <dd className="col-span-2 text-brand-charcoal font-serif text-lg">{s.v}</dd>
                </div>
              ))}
            </dl>
          </Reveal>
          <Reveal delay={150} className="lg:col-span-4 lg:col-start-9">
            <span className="overline">Applications</span>
            <h3 className="font-serif text-3xl md:text-4xl mt-4 text-brand-charcoal mb-10">Where it fits.</h3>
            <ul className="space-y-4">
              {product.applications.map((a) => (
                <li key={a} className="flex items-start gap-3 text-brand-charcoal/75 text-[15px] leading-relaxed">
                  <span className="mt-2 w-1.5 h-1.5 bg-brand-gold shrink-0" />
                  <span>{a}</span>
                </li>
              ))}
            </ul>
            <div className="mt-10 p-6 bg-brand-ivory-warm border-l-2 border-brand-gold">
              <div className="text-[11px] tracking-[0.22em] uppercase text-brand-gold mb-2">Private Label Available</div>
              <p className="text-brand-charcoal/70 text-[14px] leading-relaxed">Custom blends, packaging design and destination-specific labelling are available for buyers building their own brand.</p>
              <Link to="/private-label" className="mt-4 inline-flex items-center gap-2 text-[12px] tracking-[0.22em] uppercase text-brand-green font-semibold link-underline">
                Explore Private Label <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* RELATED */}
      <section className="py-24 bg-brand-ivory-warm">
        <div className="container-luxe">
          <Reveal className="flex items-end justify-between mb-12">
            <div>
              <span className="overline">You may also like</span>
              <h3 className="font-serif text-3xl md:text-4xl mt-4 text-brand-charcoal">Related from {label}.</h3>
            </div>
            <Link to={base} className="hidden md:inline-flex text-[12px] tracking-[0.22em] uppercase text-brand-green font-semibold link-underline">All {label}</Link>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {related.map((p, i) => (
              <Reveal key={p.slug} delay={i * 60}>
                <Link to={`${base}/${p.slug}`} className="group block" data-testid={`related-${p.slug}`}>
                  <div className="relative aspect-square overflow-hidden bg-brand-ink">
                    <div className="absolute inset-0" style={{ backgroundColor: p.color, opacity: 0.3 }} />
                    <img src={p.image} alt={p.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/70 via-transparent to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4 text-brand-ivory flex items-end justify-between">
                      <div className="font-serif text-lg">{p.name}</div>
                      <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <RequestQuoteModal open={quoteOpen} onClose={() => setQuoteOpen(false)} defaultProduct={product.name} />
    </>
  );
}
